import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { from } from 'rxjs';
import { SiteForm } from 'src/app/classes/form-groups/site-form';
import { LoadingMask } from 'src/app/classes/loading-mask';
import { Site, SiteUser } from 'src/app/classes/models';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { SiteService } from 'src/app/services/site.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';

@Component({
  selector: 'app-add-edit-site',
  templateUrl: './add-edit-site.component.html',
  styleUrls: ['./add-edit-site.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditSiteComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private SiteService: SiteService,
    private ToastService: ToastService,
    private AuthService: AuthService,
    private UtilityService: UtilityService,
    private CompanyService: CompanyService,
    private location: Location,
    private HostElement: ElementRef
  ) {
    // Not using Mailing address for site add/edit
    this.siteFormGroup.removeControl('MailingAddress');
  }

  @Input() SiteId?: number;
  @Input() CompanyId!: number;

  siteFormGroup: SiteForm = new SiteForm();
  siteUsers: SiteUser[] = [];

  country!: 'US' | 'CA';
  einMask = this.UtilityService.einMask;
  phoneMask = this.UtilityService.phoneMask;

  LocalizationInfo!: LocalizationInfo;

  saveSite() {
    const siteFormErrors = this.UtilityService.generateValidationErrorsObject(
      this.siteFormGroup
    );

    if (siteFormErrors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There is an error with one of the fields below.',
        { returnError: true }
      );
    }

    const Site: Site = this.siteFormGroup.getRawValue();

    if (!this.SiteId) {
      const siteUser = {
        SiteId: Site.id,
        UserId: this.AuthService.getUserId(),
        isPrimary: true
      };

      Site.SiteUsers = [siteUser];

      Object.assign(Site, {
        status: 'Active'
      });
    }

    const { nativeElement } = this.HostElement;
    const mask = new LoadingMask(nativeElement, { maskText: 'Saving Site...' });

    this.SiteService.saveSite(Site).subscribe({
      next: () => {
        mask.destroy();
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully saved site.'
        );
        this.back();
      },
      error: () => {
        mask.destroy();
      }
    });
  }

  back() {
    this.location.back();
  }

  loadSiteData() {
    const siteQuery = {
      id: this.SiteId,
      StreetAddress: {}
    };

    this.SiteService.getSite(
      this.UtilityService.stringifyObjectsInQueryObject(siteQuery)
    ).subscribe({
      next: (Site: Site) => {
        if (!Site) {
          throw this.ToastService.addErrorToast(
            'Error',
            'There was an error retrieving site.',
            { returnError: true }
          );
        }

        if (this.siteFormGroup) {
          this.siteFormGroup.patchValue(Site);
        }
        this._cdr.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    this.siteFormGroup.patchValue({ CompanyId: this.CompanyId });

    if (this.SiteId) {
      this.loadSiteData();
    }

    // Acquire localization info.
    from(
      companiesService.findOneById({
        pathParams: { companyId: this.CompanyId }
      })
    ).subscribe({
      next: (Company) => {
        if (!Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client information.',
            { returnError: true }
          );
        }

        const { country } = Company;

        if (!country || !new Set(['US', 'CA']).has(country)) {
          throw this.ToastService.addErrorToast(
            'Error',
            'There was an error loading country information.',
            { returnError: true }
          );
        }

        this.country = country;

        this.LocalizationInfo = this.UtilityService.getLocalizedValuesMap(
          this.country
        );
        this._cdr.markForCheck();
      }
    });
  }
}
